import { useCallback, useEffect, useState } from 'react';
import {
  listMemories,
  permanentlyDeleteMemory,
  restoreMemory,
  updateMemory,
} from '../../../api/memories';
import { loginUser, updateUserProfile } from '../../../api/user';
import type { AnonymousUser, MemoryListItem, MemoryRead } from '../../../api/types';
import { useAnonymousUser } from '../../../hooks/useAnonymousUser';
import PullToRefresh from './PullToRefresh';

type View = 'profile' | 'profile-edit' | 'profile-login' | 'deleted-list' | 'deleted-detail' | 'deleted-edit';

interface ProfileScreenProps {
  onMemoryChanged?: () => void;
}

const EMOTION_COLOR: Record<string, string> = {
  joy: '#3ECFA0',
  sadness: '#4A90E2',
  fear: '#8B7AB8',
  anger: '#FF6B6B',
};

const EMOTION_LABEL: Record<string, string> = {
  joy: 'Радость',
  sadness: 'Печаль',
  fear: 'Страх',
  anger: 'Гнев',
};

function parseDate(value: string): Date {
  return new Date(value.includes('T') ? value : `${value}T00:00:00`);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parseDate(value));
}

function toDateInput(value: string): string {
  return parseDate(value).toISOString().slice(0, 10);
}

function previewText(memory: MemoryListItem): string {
  const text = memory.story_text?.trim() || 'Текст воспоминания пока не заполнен.';
  return text.length > 110 ? `${text.slice(0, 110)}...` : text;
}

export default function ProfileScreen({ onMemoryChanged }: ProfileScreenProps) {
  const { user, userId, loading: userLoading, error: userError } = useAnonymousUser();
  const [view, setView] = useState<View>('profile');
  const [profileUser, setProfileUser] = useState<AnonymousUser | null>(null);
  const [deleted, setDeleted] = useState<MemoryListItem[]>([]);
  const [selected, setSelected] = useState<MemoryListItem | null>(null);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (user) setProfileUser(user);
  }, [user]);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2200);
  };

  const loadDeleted = useCallback(() => {
    if (!userId) return Promise.resolve();
    setLoadingDeleted(true);
    setError(null);
    return listMemories({ userId, deletedOnly: true, limit: 100 })
      .then((items) => setDeleted(items))
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Не удалось загрузить удаленные события');
      })
      .finally(() => setLoadingDeleted(false));
  }, [userId]);

  useEffect(() => {
    if (view === 'deleted-list') loadDeleted();
  }, [loadDeleted, view]);

  const openDeleted = () => {
    setView('deleted-list');
    setSelected(null);
    loadDeleted();
  };

  const handleRestore = async () => {
    if (!selected || actionLoading) return;
    setActionLoading(true);
    setError(null);
    try {
      await restoreMemory(selected.id);
      setDeleted((items) => items.filter((item) => item.id !== selected.id));
      setSelected(null);
      setView('deleted-list');
      onMemoryChanged?.();
      showToast('Воспоминание восстановлено');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось восстановить событие');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selected || actionLoading) return;
    setActionLoading(true);
    setError(null);
    try {
      await permanentlyDeleteMemory(selected.id);
      setDeleted((items) => items.filter((item) => item.id !== selected.id));
      setSelected(null);
      setView('deleted-list');
      onMemoryChanged?.();
      showToast('Воспоминание удалено окончательно');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить событие окончательно');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveEdit = async (payload: { title: string; event_date: string; story_text: string }) => {
    if (!selected || actionLoading) return;
    setActionLoading(true);
    setError(null);
    try {
      const saved = await updateMemory(selected.id, payload);
      const updated = toListItem(saved);
      setDeleted((items) => items.map((item) => (item.id === updated.id ? updated : item)));
      setSelected(updated);
      setView('deleted-detail');
      showToast('Воспоминание обновлено');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить изменения');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveProfile = async (payload: {
    display_name: string;
    email?: string | null;
    phone?: string | null;
    password?: string | null;
  }) => {
    if (!userId || actionLoading) return;
    setActionLoading(true);
    setError(null);
    try {
      const saved = await updateUserProfile(userId, payload);
      setProfileUser(saved);
      setView('profile');
      showToast('Профиль сохранен');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить профиль');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogin = async (payload: { login: string; password: string }) => {
    if (actionLoading) return;
    setActionLoading(true);
    setError(null);
    try {
      const loggedUser = await loginUser(payload);
      setProfileUser(loggedUser);
      showToast('Вход выполнен');
      setTimeout(() => window.location.reload(), 350);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось войти в профиль');
    } finally {
      setActionLoading(false);
    }
  };

  if (view === 'profile-edit') {
    return (
      <>
        <ProfileEdit
          user={profileUser}
          loading={actionLoading}
          error={error}
          onBack={() => setView('profile')}
          onSave={handleSaveProfile}
        />
        {toast && <Toast text={toast} />}
      </>
    );
  }

  if (view === 'profile-login') {
    return (
      <>
        <ProfileLogin
          loading={actionLoading}
          error={error}
          onBack={() => setView('profile')}
          onLogin={handleLogin}
        />
        {toast && <Toast text={toast} />}
      </>
    );
  }

  if (view === 'deleted-list') {
    return (
      <>
        <DeletedList
          memories={deleted}
          loading={userLoading || loadingDeleted}
          error={userError || error}
          onBack={() => setView('profile')}
          onOpen={(memory) => {
            setSelected(memory);
            setView('deleted-detail');
          }}
          onRefresh={loadDeleted}
        />
        {toast && <Toast text={toast} />}
      </>
    );
  }

  if (view === 'deleted-detail' && selected) {
    return (
      <>
        <DeletedDetail
          memory={selected}
          loading={actionLoading}
          error={error}
          onBack={() => setView('deleted-list')}
          onEdit={() => setView('deleted-edit')}
          onRestore={handleRestore}
          onPermanentDelete={handlePermanentDelete}
        />
        {toast && <Toast text={toast} />}
      </>
    );
  }

  if (view === 'deleted-edit' && selected) {
    return (
      <>
        <DeletedEdit
          memory={selected}
          loading={actionLoading}
          error={error}
          onBack={() => setView('deleted-detail')}
          onSave={handleSaveEdit}
        />
        {toast && <Toast text={toast} />}
      </>
    );
  }

  return (
    <ProfileHome
      user={profileUser}
      loading={userLoading}
      error={userError || error}
      onFillProfile={() => setView('profile-edit')}
      onLogin={() => setView('profile-login')}
      onOpenDeleted={openDeleted}
      toast={toast}
    />
  );
}

function toListItem(memory: MemoryRead): MemoryListItem {
  return {
    id: memory.id,
    recorded_at: memory.recorded_at,
    event_date: memory.event_date,
    title: memory.title,
    story_text: memory.story_text,
    status: memory.status,
    dominant_emotion: memory.dominant_emotion,
    joy_score: memory.joy_score,
    sadness_score: memory.sadness_score,
    fear_score: memory.fear_score,
    anger_score: memory.anger_score,
  };
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100%',
        background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
        paddingBottom: 'calc(32px + env(safe-area-inset-bottom))',
      }}
    >
      {children}
    </div>
  );
}

function Header({ title, onBack }: { title: string; onBack?: () => void }) {
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '56px',
        background: 'rgba(26,27,46,0.95)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(20px)',
        padding: '0 20px',
      }}
    >
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: 'absolute',
            left: '16px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <i className="ri-arrow-left-s-line" style={{ fontSize: '20px', color: '#A8B2C8' }} />
        </button>
      )}
      <span style={{ fontSize: '18px', fontWeight: 600, color: '#F5F5F7' }}>{title}</span>
    </div>
  );
}

function profileTitle(user: AnonymousUser | null): string {
  return user?.display_name || 'Новый пользователь';
}

function profileSubtitle(user: AnonymousUser | null): string {
  return user?.email || user?.phone || 'Профиль пока не заполнен';
}

function ProfileHome({
  user,
  loading,
  error,
  onFillProfile,
  onLogin,
  onOpenDeleted,
  toast,
}: {
  user: AnonymousUser | null;
  loading: boolean;
  error: string | null;
  onFillProfile: () => void;
  onLogin: () => void;
  onOpenDeleted: () => void;
  toast: string | null;
}) {
  const items = [
    {
      icon: 'ri-delete-bin-6-line',
      label: 'Удаленные события',
      sub: 'Восстановить или удалить окончательно',
      color: '#FF6B6B',
      bg: 'rgba(255,107,107,0.12)',
      onClick: onOpenDeleted,
    },
    {
      icon: 'ri-customer-service-2-line',
      label: 'Поддержка',
      sub: 'Помощь и обратная связь',
      color: '#4A90E2',
      bg: 'rgba(74,144,226,0.12)',
    },
    {
      icon: 'ri-vip-crown-line',
      label: 'Управлять подпиской',
      sub: 'Планы и доступ к функциям',
      color: '#F5A623',
      bg: 'rgba(245,166,35,0.12)',
    },
    {
      icon: 'ri-share-forward-line',
      label: 'Поделиться с другом',
      sub: 'Расскажите об Омуте Памяти',
      color: '#3ECFA0',
      bg: 'rgba(62,207,160,0.12)',
    },
  ];

  return (
    <Screen>
      <Header title="Профиль" />
      <div style={{ padding: '28px 16px 0', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {error && <ErrorPanel text={error} />}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', paddingBottom: '8px' }}>
          <div
            style={{
              width: '84px',
              height: '84px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3ECFA0 0%, #2BA87F 50%, #8B7AB8 100%)',
              padding: '2.5px',
              boxShadow: '0 8px 32px rgba(62,207,160,0.35)',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: 'linear-gradient(160deg, #232438 0%, #2D1B3D 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="ri-user-3-fill" style={{ fontSize: '34px', color: '#3ECFA0' }} />
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '20px', fontWeight: 700, color: '#F5F5F7', letterSpacing: '0.1px' }}>
              {loading ? 'Загружаем профиль...' : profileTitle(user)}
            </p>
            <p style={{ fontSize: '13px', color: '#6B7A94', marginTop: '3px' }}>
              {profileSubtitle(user)}
            </p>
          </div>
        </div>

        <button
          onClick={onFillProfile}
          style={{
            width: '100%',
            border: 'none',
            borderRadius: '20px',
            padding: '16px 18px',
            background: user?.profile_completed
              ? 'rgba(255,255,255,0.06)'
              : 'linear-gradient(135deg, #3ECFA0, #2BA87F)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '14px',
            cursor: 'pointer',
            boxShadow: user?.profile_completed ? 'none' : '0 10px 28px rgba(62,207,160,0.32)',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px', fontWeight: 700 }}>
            <i className={user?.profile_completed ? 'ri-edit-2-line' : 'ri-user-add-line'} style={{ fontSize: '20px' }} />
            {user?.profile_completed ? 'Редактировать профиль' : 'Заполнить профиль'}
          </span>
          <i className="ri-arrow-right-s-line" style={{ fontSize: '22px' }} />
        </button>

        {!user?.profile_completed && (
          <button
            onClick={onLogin}
            style={{
              width: '100%',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '18px',
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.05)',
              color: '#A8B2C8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 700,
            }}
          >
            <i className="ri-login-circle-line" style={{ fontSize: '18px', color: '#3ECFA0' }} />
            Войти в существующий профиль
          </button>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#6B7A94', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '2px', paddingLeft: '4px' }}>
            Аккаунт и настройки
          </p>
          {items.map((item) => (
            <button
              key={item.label}
              onClick={item.onClick}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '18px',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: item.onClick ? 'pointer' : 'default',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '14px',
                  background: item.bg,
                  border: `1px solid ${item.color}30`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className={item.icon} style={{ fontSize: '18px', color: item.color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7', lineHeight: 1.2 }}>
                  {item.label}
                </p>
                <p style={{ fontSize: '12px', color: '#6B7A94', marginTop: '2px' }}>
                  {item.sub}
                </p>
              </div>
              <i className="ri-arrow-right-s-line" style={{ fontSize: '18px', color: '#6B7A94' }} />
            </button>
          ))}
        </div>

        {toast && <Toast text={toast} />}
      </div>
    </Screen>
  );
}

function DeletedList({
  memories,
  loading,
  error,
  onBack,
  onOpen,
  onRefresh,
}: {
  memories: MemoryListItem[];
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onOpen: (memory: MemoryListItem) => void;
  onRefresh: () => Promise<unknown>;
}) {
  return (
    <Screen>
      <Header title="Удаленные события" onBack={onBack} />
      <PullToRefresh
        onRefresh={onRefresh}
        disabled={loading}
        style={{ height: 'calc(100dvh - 136px - env(safe-area-inset-bottom))' }}
        contentStyle={{ padding: '18px 16px 0' }}
      >
        <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {error && <ErrorPanel text={error} />}
          {loading ? (
            <StatePanel icon="ri-loader-4-line" title="Загружаем удаленные события" text="Собираем события из корзины." />
          ) : memories.length === 0 ? (
            <StatePanel icon="ri-delete-bin-6-line" title="Корзина пуста" text="Удаленные события будут появляться здесь." />
          ) : (
            memories.map((memory) => (
              <MemoryRow key={memory.id} memory={memory} onOpen={() => onOpen(memory)} />
            ))
          )}
        </div>
      </PullToRefresh>
    </Screen>
  );
}

function MemoryRow({ memory, onOpen }: { memory: MemoryListItem; onOpen: () => void }) {
  const color = memory.dominant_emotion ? EMOTION_COLOR[memory.dominant_emotion] : '#6B7A94';
  return (
    <button
      onClick={onOpen}
      style={{
        width: '100%',
        textAlign: 'left',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '18px',
        padding: '14px 16px',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, fontSize: '15px', fontWeight: 600, color: '#F5F5F7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {memory.title}
        </span>
        <i className="ri-arrow-right-s-line" style={{ fontSize: '18px', color: '#6B7A94' }} />
      </div>
      <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.5, marginBottom: '8px' }}>
        {previewText(memory)}
      </p>
      <p style={{ fontSize: '11px', color: '#6B7A94' }}>
        Событие: {formatDate(memory.event_date)}
      </p>
    </button>
  );
}

function ProfileEdit({
  user,
  loading,
  error,
  onBack,
  onSave,
}: {
  user: AnonymousUser | null;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onSave: (payload: {
    display_name: string;
    email?: string | null;
    phone?: string | null;
    password?: string | null;
  }) => void;
}) {
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [password, setPassword] = useState('');
  const needsPassword = !user?.has_password;
  const canSave = displayName.trim().length > 0
    && (email.trim().length > 0 || phone.trim().length > 0)
    && (!needsPassword || password.length >= 8)
    && (!password || password.length >= 8);

  return (
    <Screen>
      <Header title={user?.profile_completed ? 'Редактировать профиль' : 'Заполнить профиль'} onBack={onBack} />
      <div style={{ padding: '18px 16px 0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {error && <ErrorPanel text={error} />}
        <div style={{ background: 'rgba(62,207,160,0.08)', border: '1px solid rgba(62,207,160,0.22)', borderRadius: '18px', padding: '14px 16px' }}>
          <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.6 }}>
            Логином будет email или телефон. Пароль нужен, чтобы потом войти в этот же профиль с другого устройства.
          </p>
        </div>
        <Field label="Имя">
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Как вас зовут"
            style={inputStyle}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@example.com"
            style={inputStyle}
          />
        </Field>
        <Field label="Телефон">
          <input
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+7 999 123-45-67"
            style={inputStyle}
          />
        </Field>
        <Field label={needsPassword ? 'Пароль' : 'Новый пароль'}>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={needsPassword ? 'Минимум 8 символов' : 'Оставьте пустым, если не меняете'}
            style={inputStyle}
          />
        </Field>
        <button
          disabled={!canSave || loading}
          onClick={() => onSave({
            display_name: displayName.trim(),
            email: email.trim() || null,
            phone: phone.trim() || null,
            password: password || null,
          })}
          style={{
            width: '100%',
            height: '52px',
            borderRadius: '16px',
            border: 'none',
            background: canSave && !loading ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)' : 'rgba(255,255,255,0.08)',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 700,
            cursor: canSave && !loading ? 'pointer' : 'default',
            opacity: canSave && !loading ? 1 : 0.55,
          }}
        >
          {loading ? 'Сохраняем...' : 'Сохранить профиль'}
        </button>
      </div>
    </Screen>
  );
}

function ProfileLogin({
  loading,
  error,
  onBack,
  onLogin,
}: {
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onLogin: (payload: { login: string; password: string }) => void;
}) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const canLogin = login.trim().length >= 3 && password.length >= 8;

  return (
    <Screen>
      <Header title="Войти в профиль" onBack={onBack} />
      <div style={{ padding: '18px 16px 0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {error && <ErrorPanel text={error} />}
        <div style={{ background: 'rgba(62,207,160,0.08)', border: '1px solid rgba(62,207,160,0.22)', borderRadius: '18px', padding: '14px 16px' }}>
          <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.6 }}>
            Используйте email или телефон, который указан в профиле.
          </p>
        </div>
        <Field label="Логин">
          <input
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            placeholder="Email или телефон"
            style={inputStyle}
          />
        </Field>
        <Field label="Пароль">
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Минимум 8 символов"
            style={inputStyle}
          />
        </Field>
        <button
          disabled={!canLogin || loading}
          onClick={() => onLogin({ login: login.trim(), password })}
          style={{
            width: '100%',
            height: '52px',
            borderRadius: '16px',
            border: 'none',
            background: canLogin && !loading ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)' : 'rgba(255,255,255,0.08)',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 700,
            cursor: canLogin && !loading ? 'pointer' : 'default',
            opacity: canLogin && !loading ? 1 : 0.55,
          }}
        >
          {loading ? 'Входим...' : 'Войти'}
        </button>
      </div>
    </Screen>
  );
}

function DeletedDetail({
  memory,
  loading,
  error,
  onBack,
  onEdit,
  onRestore,
  onPermanentDelete,
}: {
  memory: MemoryListItem;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onEdit: () => void;
  onRestore: () => void;
  onPermanentDelete: () => void;
}) {
  const color = memory.dominant_emotion ? EMOTION_COLOR[memory.dominant_emotion] : '#6B7A94';
  const emotionLabel = memory.dominant_emotion ? EMOTION_LABEL[memory.dominant_emotion] : 'Не определено';
  return (
    <Screen>
      <Header title="Удаленное событие" onBack={onBack} />
      <div style={{ padding: '18px 16px 0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {error && <ErrorPanel text={error} />}
        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color }} />
            <span style={{ fontSize: '13px', color, fontWeight: 600 }}>{emotionLabel}</span>
          </div>
          <h2 style={{ fontSize: '22px', lineHeight: 1.3, color: '#F5F5F7', marginBottom: '8px' }}>{memory.title}</h2>
          <p style={{ fontSize: '13px', color: '#6B7A94', marginBottom: '16px' }}>
            Событие: {formatDate(memory.event_date)}
          </p>
          <p style={{ fontSize: '15px', color: '#A8B2C8', lineHeight: 1.7 }}>
            {memory.story_text || 'Текст воспоминания пока не заполнен.'}
          </p>
        </div>

        <ActionButton label="Восстановить" icon="ri-refresh-line" color="#3ECFA0" disabled={loading} onClick={onRestore} />
        <ActionButton label="Редактировать" icon="ri-edit-2-line" color="#4A90E2" disabled={loading} onClick={onEdit} />
        <ActionButton label="Удалить окончательно" icon="ri-delete-bin-line" color="#FF6B6B" disabled={loading} onClick={onPermanentDelete} />
      </div>
    </Screen>
  );
}

function DeletedEdit({
  memory,
  loading,
  error,
  onBack,
  onSave,
}: {
  memory: MemoryListItem;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onSave: (payload: { title: string; event_date: string; story_text: string }) => void;
}) {
  const [title, setTitle] = useState(memory.title);
  const [eventDate, setEventDate] = useState(toDateInput(memory.event_date));
  const [text, setText] = useState(memory.story_text || '');
  const canSave = title.trim().length > 0 && text.trim().length > 0 && eventDate.length > 0;

  return (
    <Screen>
      <Header title="Редактировать" onBack={onBack} />
      <div style={{ padding: '18px 16px 0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {error && <ErrorPanel text={error} />}
        <Field label="Название">
          <input value={title} onChange={(event) => setTitle(event.target.value)} style={inputStyle} />
        </Field>
        <Field label="Дата события">
          <input type="date" value={eventDate} onChange={(event) => setEventDate(event.target.value)} style={inputStyle} />
        </Field>
        <Field label="История">
          <textarea value={text} onChange={(event) => setText(event.target.value)} rows={8} style={{ ...inputStyle, height: '180px', resize: 'vertical', lineHeight: 1.6 }} />
        </Field>
        <button
          disabled={!canSave || loading}
          onClick={() => onSave({ title: title.trim(), event_date: eventDate, story_text: text.trim() })}
          style={{
            width: '100%',
            height: '52px',
            borderRadius: '16px',
            border: 'none',
            background: canSave && !loading ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)' : 'rgba(255,255,255,0.08)',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 700,
            cursor: canSave && !loading ? 'pointer' : 'default',
            opacity: canSave && !loading ? 1 : 0.55,
          }}
        >
          {loading ? 'Сохраняем...' : 'Сохранить'}
        </button>
      </div>
    </Screen>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <span style={{ fontSize: '13px', fontWeight: 600, color: '#A8B2C8' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.06)',
  color: '#F5F5F7',
  padding: '14px 16px',
  fontSize: '15px',
  outline: 'none',
} as const;

function ActionButton({
  label,
  icon,
  color,
  disabled,
  onClick,
}: {
  label: string;
  icon: string;
  color: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        height: '52px',
        borderRadius: '16px',
        border: `1px solid ${color}40`,
        background: `${color}18`,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '15px',
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <i className={icon} style={{ fontSize: '18px' }} />
      {label}
    </button>
  );
}

function StatePanel({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div style={{ padding: '64px 24px', textAlign: 'center', color: '#6B7A94' }}>
      <i className={icon} style={{ fontSize: '34px', color: '#3ECFA0' }} />
      <p style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F7', marginTop: '14px' }}>{title}</p>
      <p style={{ fontSize: '14px', lineHeight: 1.6, marginTop: '8px' }}>{text}</p>
    </div>
  );
}

function ErrorPanel({ text }: { text: string }) {
  return (
    <div style={{ background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '16px', padding: '14px 16px', color: '#FFD1D1', fontSize: '13px', lineHeight: 1.5 }}>
      {text}
    </div>
  );
}

function Toast({ text }: { text: string }) {
  return (
    <div style={{ position: 'fixed', left: '18px', right: '18px', bottom: 'calc(96px + env(safe-area-inset-bottom))', zIndex: 250, borderRadius: '18px', padding: '14px 16px', background: 'rgba(62,207,160,0.96)', color: '#fff', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 12px 32px rgba(62,207,160,0.35)' }}>
      <i className="ri-checkbox-circle-line" style={{ fontSize: '20px', flexShrink: 0 }} />
      <span style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.45 }}>{text}</span>
    </div>
  );
}
